import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  Button,
  Modal,
  Dropdown,
  TextInput,
  Select,
} from "flowbite-react";
import { Store } from "../context/store";
import { IoMdAddCircleOutline } from "react-icons/io";
import { FiFilter } from "react-icons/fi";
import apiRequest from "../utils/apiRequest";
import { useSearchParams } from "react-router-dom";
import { confirmDialog } from "../components/ConfirmDialog";
import DashboardLayout from "../components/layout/DashboardLayout";
import DataTable from "../components/data/DataTable";
import SessionExpiredModal from "../components/SessionExpiredModal";
import { useSessionExpiry } from "../hooks/useSessionExpiry";
import { formatDateTime } from "../utils/formatters";

function Tasks() {
  let store = useContext(Store);
  let [baseUrl] = store.url;
  let [tasks, setTasks] = useState([]);
  let [singleTask, setSingleTask] = useState({});
  let [loading1, setLoading1] = useState(false);
  let [loading2, setLoading2] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModal1, setOpenModal1] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [totalPages, setTotalPages] = useState(1);
  const page = parseInt(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(page);
  let [status, setStatus] = useState("pending");
  const [error, setError] = useState("");
  let [staff, setStaff] = useState([]);
  const [task, setTask] = useState({
    userId: "",
    task: "",
    comment: "",
  });
  let [errorColor, setErrorColor] = useState("");
  let [loading3] = useState(false);
  const { isSessionExpired, handleAuthError, confirmSessionExpired } =
    useSessionExpiry();
  const onPageChange = (page) => {
    setSearchParams({ page: page });
    setCurrentPage(page);
  };

  function onCloseModal() {
    setOpenModal(false);
    setSingleTask({});
  }

  function onCloseModal1() {
    setOpenModal1(false);
    setTask({ userId: "", task: "", comment: "" });
  }

  const loadTasks = useCallback(async () => {
    setLoading1(true);
    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/tasks?page=${page}&status=${status}`;
      const { data } = await apiRequest(url, "GET", null, token);
      setTasks(data.tasks);
      setTotalPages(data.totalPages);
    } catch (error) {
      handleAuthError(error);
    }
  }, [baseUrl, status, page, handleAuthError]);

  const loadStaff = useCallback(async () => {
    setLoading1(true);
    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/staff`;
      const { data } = await apiRequest(url, "GET", null, token);
      setStaff(data);
    } catch (error) {
      handleAuthError(error);
    }
  }, [baseUrl, handleAuthError]);

  useEffect(() => {
    Promise.all([loadTasks(), loadStaff()])
      .then(() => setLoading1(false))
      .catch(() => setLoading1(false));
  }, [loadTasks, loadStaff]);

  const respondToTask = async (a, b) => {
    setLoading2(true);
    try {
      let token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/task/${a}`;
      const { data } = await apiRequest(url, "PUT", { status: b }, token);
      setSingleTask(data);
      setTasks((prevMessages) =>
        prevMessages.map((singleTask) =>
          singleTask.id === data.id ? data : singleTask
        )
      );
      setLoading2(false);
    } catch (error) {
      setLoading2(false);
      console.log(error);
    }
  };

  let displayOnModal = (e) => {
    setSingleTask(e);
    setOpenModal(true);
  };

  const createNewTask = async () => {
    if (!task.userId) {
      setErrorColor("red");
      setError("Please select a staff to assign task to.");
      return;
    }
    if (!task.task) {
      setErrorColor("red");
      setError("Please add the task.");
      return;
    }
    await confirmDialog("Are you sure you want to assign a task?", {
      title: "Assign Task",
      confirmText: "Yes, assign",
      color: "success",
      loadingText: "Assigning...",
      onConfirm: async () => {
        try {
          let token = localStorage.getItem("royal-token");
          const url = `${baseUrl}/task`;
          let { message } = await apiRequest(url, "POST", task, token);
          setError(message);
          setTask({
            userId: "",
            task: "",
            comment: "",
          });
          loadTasks();
          setLoading1(false);
        } catch (error) {
          setError(error.message);
          console.log(error);
        }
      },
    });
  };

  const taskColumns = [
    {
      key: "assignedBy",
      header: "Assigned By",
      className: "whitespace-nowrap font-medium text-gray-900 dark:text-white",
      render: (task) =>
        `${task.assignedById?.firstName || ""} ${
          task.assignedById?.lastName || ""
        }`,
    },
    {
      key: "assignedTo",
      header: "Assigned To",
      className: "whitespace-nowrap font-medium text-gray-900 dark:text-white",
      render: (task) =>
        `${task.userId?.firstName || ""} ${task.userId?.lastName || ""}`,
    },
    {
      key: "task",
      header: "Description",
      className: "text-ellipsis",
    },
    {
      key: "submittedOn",
      header: "Submitted On",
      className: "text-ellipsis",
      render: (task) => formatDateTime(task?.submittedOn),
    },
    {
      key: "status",
      header: "Status",
      className: "capitalize",
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      render: (task) => (
        <button
          type="button"
          className="font-medium text-cyan-600 hover:underline dark:text-cyan-500"
          onClick={() => displayOnModal(task)}
        >
          View
        </button>
      ),
    },
  ];

  const tableRows = tasks.map((task) => ({
    ...task,
    rowClassName: `bg-white dark:border-gray-700 dark:bg-gray-800 ${
      task.status === "completed"
        ? "read-msgs"
        : task.status === "cancelled"
        ? "reads-msgs"
        : ""
    }`,
  }));

  return (
    <>
      <title>Tasks</title>
      <meta
        name="description"
        content="Tasks Page to the Royalmines Property Office"
      />
      <DashboardLayout active="tasks" animation="flip-up">
            <div className="mb-4">
              <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
                Tasks
              </h5>
              <div className="flex flex-wrap gap-2 mt-5 justify-end">
                <Button color="success" onClick={() => setOpenModal1(true)}>
                  <IoMdAddCircleOutline className="me-2" />
                  New Task
                </Button>
                <Dropdown
                  label={
                    <Button className="bg-#6eb5ff">
                      <FiFilter className="me-2" /> Filter By
                    </Button>
                  }
                  dismissOnClick={true}
                  inline
                >
                  <Dropdown.Item
                    className={status === "" ? "active-dropdown" : ""}
                    onClick={() => {
                      setSearchParams({ page: 1 });
                      setStatus("");
                      setCurrentPage(1);
                    }}
                  >
                    All
                  </Dropdown.Item>
                  <Dropdown.Item
                    className={status === "pending" ? "active-dropdown" : ""}
                    onClick={() => {
                      setSearchParams({ page: 1 });
                      setStatus("pending");
                      setCurrentPage(1);
                    }}
                  >
                    Pending
                  </Dropdown.Item>
                  <Dropdown.Item
                    className={status === "submitted" ? "active-dropdown" : ""}
                    onClick={() => {
                      setSearchParams({ page: 1 });
                      setStatus("submitted");
                      setCurrentPage(1);
                    }}
                  >
                    Submitted
                  </Dropdown.Item>
                  <Dropdown.Item
                    className={status === "completed" ? "active-dropdown" : ""}
                    onClick={() => {
                      setSearchParams({ page: 1 });
                      setStatus("completed");
                      setCurrentPage(1);
                    }}
                  >
                    Completed
                  </Dropdown.Item>
                  <Dropdown.Item
                    className={status === "cancelled" ? "active-dropdown" : ""}
                    onClick={() => {
                      setSearchParams({ page: 1 });
                      setStatus("cancelled");
                      setCurrentPage(1);
                    }}
                  >
                    Cancelled
                  </Dropdown.Item>
                </Dropdown>
              </div>
            </div>
            <DataTable
              columns={taskColumns}
              rows={tableRows}
              loading={loading1}
              emptyTitle="No tasks found"
              emptyDescription={
                status
                  ? `There are no ${status} tasks on this page.`
                  : "Assigned tasks will appear here once they are created."
              }
              wrapperClassName="h-[45vh]"
              pagination={{
                currentPage,
                totalPages,
                onPageChange,
              }}
            />
      </DashboardLayout>

      {/* modal for task */}
      <Modal
        show={openModal}
        size="md"
        onClose={onCloseModal}
        popup
        theme={{
          root: {
            base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          },
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
          },
          // The backdrop needs a high z-index too
          backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white">
              <strong>Assigned By: </strong>
              {singleTask?.assignedById?.firstName}{" "}
              {singleTask?.assignedById?.lastName}
            </h3>
            <h3 className="text-xl font-medium text-gray-900 dark:text-white">
              <strong>Assigned To: </strong>
              {singleTask?.userId?.firstName} {singleTask?.userId?.lastName}
            </h3>
            <div>
              <h4>
                <strong>Description: </strong>
                {singleTask?.task}
              </h4>
            </div>
            <div>
              <h4>
                <strong>Date Created: </strong>
                {formatDateTime(singleTask?.createdAt)}
              </h4>
            </div>
            <div>
              <h4 className="capitalize">
                <strong>Status: </strong> {singleTask?.status}
              </h4>
            </div>
            {singleTask?.comments?.length > 0 && (
              <div>
                <h4>
                  <strong>Comments: </strong>{" "}
                  {singleTask?.comments?.map((a, b) => {
                    return (
                      <li className="mb-3 bg-slate-200 p-2 rounded">
                        Comment: {a.comment}
                        <br />
                        By: {a.by.firstName} {a.by.lastName}
                        <br />
                        Timestamp: {formatDateTime(a.time)}
                      </li>
                    );
                  })}
                </h4>
              </div>
            )}
            {singleTask?.status === "pending" ||
              (singleTask?.status === "submitted" && (
                <div className="w-full flex justify-center gap-3">
                  <Button
                    color="failure"
                    onClick={() => respondToTask(singleTask.id, "cancelled")}
                    disabled={loading2}
                  >
                    Cancel
                  </Button>
                  {singleTask?.status !== "submitted" && (
                    <Button
                      color="light"
                      onClick={() => respondToTask(singleTask.id, "submitted")}
                      disabled={loading2}
                    >
                      Mark as Submitted
                    </Button>
                  )}
                  <Button
                    color="success"
                    onClick={() => respondToTask(singleTask.id, "completed")}
                    disabled={loading2}
                  >
                    Complete
                  </Button>
                </div>
              ))}
          </div>
        </Modal.Body>
      </Modal>

      {/* modal for add task */}
      <Modal
        show={openModal1}
        size="md"
        onClose={onCloseModal1}
        popup
        theme={{
          root: {
            base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          },
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
          },
          // The backdrop needs a high z-index too
          backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white text-center">
              Add New Task
            </h3>
            <div className="flex justify-center">
              <small
                style={{
                  color: errorColor,
                  textAlign: "center",
                }}
              >
                {error}
              </small>
            </div>
            <div>
              <TextInput
                id="task"
                placeholder="Task Description"
                value={task.task}
                onChange={(event) =>
                  setTask({
                    ...task,
                    task: event.target.value,
                  })
                }
                required
              />
            </div>

            <div>
              <TextInput
                id="comment"
                placeholder="Add Comment if any."
                value={task.comment}
                onChange={(event) =>
                  setTask({
                    ...task,
                    comment: event.target.value,
                  })
                }
                required
              />
            </div>

            <div>
              <Select
                id="staffId"
                onChange={(event) =>
                  setTask({
                    ...task,
                    userId: event.target.value,
                  })
                }
                required
                style={{ textTransform: "capitalize" }}
              >
                <option value="">Select a Staff</option>
                {staff?.map((a, b) => {
                  return (
                    <option key={b} value={a.id}>
                      {a.firstName} {a.lastName}
                    </option>
                  );
                })}
              </Select>
            </div>
            <div className="w-full flex justify-center">
              <Button disabled={loading3} onClick={() => createNewTask()}>
                {loading3 ? "Loading..." : "Assign Task"}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <SessionExpiredModal
        open={isSessionExpired}
        onOk={confirmSessionExpired}
      />
    </>
  );
}

export default Tasks;
